import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle, Plus, X, Warning } from "@phosphor-icons/react";
import { useSector } from "@/lib/sectorContext";

/**
 * Modo Teste — página dedicada (movida da Operação → Identidade
 * para a categoria Configuração no menu lateral, fev/2026).
 *
 * Responsabilidades:
 * 1) Alternar entre Modo Teste (whitelist) e Modo Produção (captura ampla).
 * 2) Gerenciar a whitelist de telefones autorizados quando em Modo Teste.
 *
 * Backend:
 *  - PUT  /api/reception/settings        — grava `mode`
 *  - POST /api/reception/test-phones     — adiciona telefone
 *  - DEL  /api/reception/test-phones/:p  — remove telefone
 *
 * Regra de negócio: ativar `test_safe` sem nenhum telefone na whitelist
 * deixa a IA muda. O backend bloqueia (400). Surfaceamos a regra na UI.
 */

const MODES = [
  {
    value: "test_safe",
    title: "Modo Teste (whitelist)",
    desc:
      "A IA SÓ responde aos telefones que você cadastrar abaixo. Ideal pra testar antes " +
      "de soltar pra base inteira — nenhum cliente real é impactado.",
  },
  {
    value: "production_lead_capture",
    title: "Modo Produção",
    desc:
      "A IA responde, agenda e atende clientes 24/7. Você só entra quando ela escalar pra " +
      "operador humano (Modelo B — Perguntas pendentes).",
  },
];

function formatPhone(digits) {
  if (!digits) return "";
  const d = String(digits);
  if (d.length === 13) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
  return d;
}

export default function TestMode() {
  const { enablesKm, isChurch, isMandate, labels } = useSector();
  const [settings, setSettings] = useState(null);
  const [newPhone, setNewPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [phoneBusy, setPhoneBusy] = useState(false);
  // Quando o operador clica "Modo Teste" sem ter telefone, marcamos a
  // INTENÇÃO num ref (não em state) pra não depender do estado serializado
  // que vai sofrer atualizações concorrentes via `setSettings(prev => …)`.
  // Assim, após cadastrar o primeiro telefone, sabemos com certeza se
  // ainda precisamos efetivar o PUT do modo no backend.
  const pendingTestSafe = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get("/reception/settings");
      setSettings(data);
    } catch {
      toast.error("Não consegui carregar as configurações.");
    }
  };

  useEffect(() => { load(); }, []);

  const setMode = async (mode) => {
    if (!settings || settings.mode === mode || saving) return;
    if (mode === "test_safe" && (settings.test_safe_allowed_phones || []).length === 0) {
      // Mostra visualmente como rascunho; backend ainda não foi mudado.
      // Quando o primeiro telefone for cadastrado, efetiva o PUT.
      pendingTestSafe.current = true;
      setSettings((prev) => ({ ...prev, mode }));
      toast.warning("Cadastre pelo menos 1 telefone — o Modo Teste entra em vigor automaticamente.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/reception/settings", { mode });
      pendingTestSafe.current = false;
      setSettings(data);
      toast.success(
        mode === "production_lead_capture"
          ? "Modo Produção ativado — IA capturando leads 24/7."
          : "Modo Teste ativado — só os números da whitelist serão respondidos.",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui salvar agora.");
    } finally {
      setSaving(false);
    }
  };

  const addTestPhone = async () => {
    const raw = (newPhone || "").trim();
    if (!raw || phoneBusy) return;
    setPhoneBusy(true);
    try {
      const { data: out } = await api.post("/reception/test-phones", { phone: raw });
      const phones = out.test_safe_allowed_phones || [];
      setSettings((prev) => ({ ...prev, test_safe_allowed_phones: phones }));
      setNewPhone("");
      toast.success("Telefone de teste cadastrado.");
      // Se o operador alternou pra test_safe sem números (intenção em ref)
      // e agora cadastrou o primeiro, efetiva o modo no backend AGORA.
      if (pendingTestSafe.current && phones.length >= 1) {
        try {
          const { data } = await api.put("/reception/settings", { mode: "test_safe" });
          pendingTestSafe.current = false;
          setSettings(data);
          toast.success("Modo Teste ativado — só os números da whitelist serão respondidos.");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Telefone salvou, mas não consegui ativar o modo.");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cadastrar telefone.");
    } finally {
      setPhoneBusy(false);
    }
  };

  const removeTestPhone = async (phone) => {
    if (phoneBusy) return;
    setPhoneBusy(true);
    try {
      const { data: out } = await api.delete(
        `/reception/test-phones/${encodeURIComponent(phone)}`,
      );
      setSettings((prev) => ({ ...prev, test_safe_allowed_phones: out.test_safe_allowed_phones || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover telefone.");
    } finally {
      setPhoneBusy(false);
    }
  };

  if (!settings) return null;
  const phones = settings.test_safe_allowed_phones || [];
  const isTestMode = settings.mode === "test_safe";

  return (
    <>
      <Topbar
        title="Modo Teste"
        subtitle="Controle se a IA responde só números autorizados ou captura leads 24/7."
      />
      <div className="p-6 md:p-8 space-y-6 max-w-4xl" data-testid="test-mode-page">
        {/* ── Escolha do modo ─────────────────────────────────── */}
        <section className="rounded-2xl border border-slate-100 bg-white p-5 space-y-4">
          <header className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-slate-700" />
            <h3 className="text-sm font-semibold text-slate-900 tracking-tight">
              Como o AgentUS deve se comportar?
            </h3>
          </header>

          <div className="grid md:grid-cols-2 gap-3">
            {MODES.map((m) => {
              const active = settings.mode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMode(m.value)}
                  disabled={saving}
                  data-testid={`test-mode-card-${m.value}`}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    active
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                  } disabled:opacity-60`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${active ? "bg-white" : "bg-slate-300"}`} />
                    <span className="text-[13px] font-semibold tracking-tight">{m.title}</span>
                    {active && (
                      <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white text-slate-900 text-[10px] font-medium">
                        <CheckCircle size={10} weight="fill" /> ATIVO
                      </span>
                    )}
                  </div>
                  <p
                    className={`text-[12px] mt-1.5 leading-relaxed ${
                      active ? "text-white/80" : "text-slate-500"
                    }`}
                  >
                    {m.desc}
                  </p>
                </button>
              );
            })}
          </div>
        </section>

        {/* ── Whitelist ───────────────────────────────────────── */}
        {isTestMode && (
          <section
            data-testid="test-mode-whitelist-panel"
            className="rounded-2xl border border-slate-100 bg-slate-50/60 p-5 space-y-3"
          >
            <div className="space-y-1">
              <h4 className="text-[13px] font-semibold text-slate-900 tracking-tight">
                Telefones autorizados pra teste
              </h4>
              <p className="text-[12px] text-slate-600 leading-relaxed">
                No <strong>Modo Teste</strong>, sua IA <strong>SÓ responde</strong> aos números cadastrados aqui.
                Qualquer outro número — mesmo clientes já importados — é silenciosamente ignorado.
                Cadastre seu próprio WhatsApp ou de alguém de confiança, mande{" "}
                <code className="px-1 py-0.5 rounded bg-white text-slate-900 text-[11px]">oi</code>{" "}
                desse número e veja a IA em ação sem risco.
              </p>
              <p className="text-[11px] text-slate-400 mt-1">
                Limite: até 5 telefones. Use DDD + número. Ex.:{" "}
                <code className="bg-white px-1 py-0.5 rounded text-[10px]">11 99999-8888</code>
              </p>
            </div>

            {phones.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="test-mode-phones-list">
                {phones.map((p, i) => (
                  <span
                    key={p}
                    data-testid={`test-mode-phone-chip-${i}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 text-white text-[12px] font-mono"
                  >
                    {formatPhone(p)}
                    <button
                      type="button"
                      onClick={() => removeTestPhone(p)}
                      disabled={phoneBusy}
                      className="hover:opacity-70 disabled:opacity-40"
                      aria-label="remover"
                      data-testid={`test-mode-phone-remove-${i}`}
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="tel"
                inputMode="tel"
                data-testid="test-mode-phone-input"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTestPhone(); } }}
                placeholder="11 99999-8888"
                disabled={phoneBusy || phones.length >= 5}
                className="flex-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400"
              />
              <button
                type="button"
                onClick={addTestPhone}
                disabled={phoneBusy || !newPhone.trim() || phones.length >= 5}
                data-testid="test-mode-phone-add"
                className="px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] inline-flex items-center gap-1 disabled:opacity-40"
              >
                <Plus size={12} weight="bold" /> Adicionar
              </button>
            </div>

            {phones.length === 0 && (
              <p
                data-testid="test-mode-empty-warning"
                className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center gap-2"
              >
                <Warning size={14} weight="fill" /> Sem telefones cadastrados, o Modo Teste não vale —
                a IA fica muda. Cadastre pelo menos 1 número acima.
              </p>
            )}
            {phones.length >= 5 && (
              <p className="text-[11px] text-slate-500">
                Limite de 5 telefones atingido. Remova um antes de adicionar outro.
              </p>
            )}
          </section>
        )}

        {/* ── Contexto pedagógico ─────────────────────────────── */}
        <section className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
          <h4 className="text-sm font-semibold text-slate-900 mb-2">
            Como isso afeta o atendimento na prática?
          </h4>
          <ul className="text-xs text-slate-600 space-y-1.5 leading-relaxed">
            <li>
              <strong>Modo Teste</strong> · Use enquanto você tá conectando o WhatsApp, importando a base
              ou validando templates. Mensagens fora da whitelist são silenciosamente descartadas
              (e ficam registradas pra você consultar depois).
            </li>
            <li>
              <strong>Modo Produção</strong> · Use quando você quer que o AgentUS receba e cadastre
              {isChurch
                ? " novos irmãos(ãs) sozinho — coleta nome ao longo da conversa, acolhe o motivo do contato e já guarda o(a) membro(a) na sua base automaticamente."
                : isMandate
                  ? " novos cidadãos sozinho — coleta nome ao longo da conversa, registra demanda/tema de interesse e já guarda o cidadão na base do gabinete automaticamente."
                  : enablesKm
                    ? ` novos clientes sozinho — coleta nome e ${labels?.assetSingular?.(false) || "veículo"} ao longo da conversa e já transforma cada lead em cliente da sua carteira automaticamente.`
                    : " novos clientes sozinho — coleta nome ao longo da conversa e já transforma cada lead em cliente da sua carteira automaticamente."}
            </li>
          </ul>
        </section>
      </div>
    </>
  );
}
